import { useEffect, useState } from "react";
import { Search, Download, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE, apiError } from "@/lib/api";
import { PageTitle } from "@/components/brand";
import TxTable, { TxDetails } from "@/components/TxTable";
import { Skeleton } from "@/components/ui/skeleton";

const FILTERS = [["all", "All", {}], ["deposits", "Deposits", { type: "deposit" }], ["withdrawals", "Withdrawals", { type: "withdrawal" }],
  ["pending", "Pending", { status: "pending" }], ["completed", "Completed", { status: "completed" }], ["failed", "Failed", { status: "failed" }]];

export default function Transactions() {
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [view, setView] = useState(null);

  const params = { ...FILTERS.find((f) => f[0] === filter)[2], q: q || undefined, date_from: from || undefined, date_to: to || undefined };
  const key = JSON.stringify(params);
  useEffect(() => { setPage(1); }, [key]);
  useEffect(() => {
    setData(null); setError("");
    const t = setTimeout(() => api.get("/transactions", { params: { ...JSON.parse(key), page, limit: 10 } }).then((r) => setData(r.data)).catch((e) => setError(apiError(e))), 250);
    return () => clearTimeout(t);
  }, [key, page]);

  const exportCsv = async () => {
    try {
      const r = await api.get("/transactions/export.csv", { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "crbets-transactions.csv"; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };

  return (
    <div data-testid="transactions-page">
      <PageTitle crumbs={["Account", "Transaction History"]} title="Transaction History" subtitle="Search, filter and export your wallet activity."
        right={<button data-testid="export-csv-btn" onClick={exportCsv} className="btn-outline text-sm" title={`${API_BASE}/transactions/export.csv`}><Download className="h-4 w-4" />Export CSV</button>} />
      <div className="scrollbar-none mb-4 flex gap-2 overflow-x-auto">
        {FILTERS.map(([k, l]) => <button key={k} data-testid={`tx-filter-${k}`} onClick={() => setFilter(k)} className={`shrink-0 rounded-full border px-4 py-1.5 text-sm font-semibold ${filter === k ? "border-cr-gold bg-cr-gold text-black" : "border-cr-line text-cr-ink/80"}`}>{l}</button>)}
      </div>
      <div className="mb-6 grid gap-3 md:grid-cols-[1fr_auto_auto]">
        <div className="relative"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-cr-muted" /><input data-testid="tx-search-input" className="input-cr pl-10" placeholder="Search by transaction ID or method" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <input type="date" data-testid="tx-date-from" aria-label="From date" className="input-cr" value={from} onChange={(e) => setFrom(e.target.value)} />
        <input type="date" data-testid="tx-date-to" aria-label="To date" className="input-cr" value={to} onChange={(e) => setTo(e.target.value)} />
      </div>
      {error ? <div data-testid="tx-error" className="flex items-center gap-2 rounded-lg border border-cr-danger/40 bg-cr-danger/10 p-4 text-sm text-cr-danger"><AlertTriangle className="h-4 w-4" />{error}</div> :
        !data ? <div className="space-y-2">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-12 bg-cr-elevated" />)}</div> : (
          <>
            <TxTable items={data.items} onView={setView} />
            <div className="mt-5 flex items-center justify-between text-sm text-cr-muted">
              <span data-testid="tx-total">{data.total} transactions</span>
              <div className="flex items-center gap-3">
                <button data-testid="tx-prev-page" disabled={page <= 1} onClick={() => setPage(page - 1)} className="btn-outline px-3 py-1.5 text-xs">Prev</button>
                <span data-testid="tx-page-indicator">Page {data.page} / {data.pages}</span>
                <button data-testid="tx-next-page" disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="btn-outline px-3 py-1.5 text-xs">Next</button>
              </div>
            </div>
          </>
        )}
      <TxDetails tx={view} onClose={() => setView(null)} />
    </div>
  );
}
